import * as React from "react";
import Dialog from "@src/components/Dialog";
import { GetEventRecommendationListsQuery } from "@src/@types/api.d";
import {
  BedDouble,
  BookHeart,
  Car,
  ChevronLeft,
  ChevronRight,
  CookingPot,
  ExternalLink,
  Image as ImageIcon,
  MapPin,
  Phone,
  Video,
} from "lucide-react";

type Props = {
  eventId: string | number;
  recommendationData: GetEventRecommendationListsQuery;
};

type RouteRecommendationList = NonNullable<
  GetEventRecommendationListsQuery["getEventRecommendationLists"][number]
>;

type RouteRecommendationItem = NonNullable<
  RouteRecommendationList["items"][number]
>;

type RecommendationCategory =
  | "VENUE"
  | "CATERING"
  | "FOOD"
  | "ACCOMMODATION"
  | "TRANSPORTATION"
  | "OTHER"
  | string;

type RecommendationMediaType = "IMAGE" | "VIDEO" | string;

type RecommendationItem = {
  id: string;
  name: string;
  description?: string | null;
  contactInfo?: string | null;
  link?: string | null;
  mediaUrl?: string | null;
  mediaType?: RecommendationMediaType | null;
  createdAt?: string | null;
  updatedAt?: string | null;
};

type RecommendationList = {
  id: string;
  name: string;
  category?: RecommendationCategory | null;
  description?: string | null;
  items?: RecommendationItem[] | null;
  createdAt?: string | null;
  updatedAt?: string | null;
};

const CATEGORY_LABELS: Record<string, string> = {
  VENUE: "Venue",
  CATERING: "Catering",
  FOOD: "Food",
  ACCOMMODATION: "Accommodation",
  TRANSPORTATION: "Transportation",
  OTHER: "Other",
};

const CATEGORY_META: Record<
  string,
  { icon: React.ComponentType<{ className?: string }> }
> = {
  VENUE: { icon: MapPin },
  CATERING: { icon: CookingPot },
  FOOD: { icon: CookingPot },
  ACCOMMODATION: { icon: BedDouble },
  TRANSPORTATION: { icon: Car },
  OTHER: { icon: BookHeart },
};

const toTitleCase = (value?: string | null): string => {
  if (!value) {
    return "Untitled List";
  }

  return value
    .toLowerCase()
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const getCategoryLabel = (category?: string | null): string => {
  if (!category) {
    return "Other";
  }

  return CATEGORY_LABELS[category] ?? toTitleCase(category.replace(/_/g, " "));
};

const getCategoryMeta = (category?: string | null) => {
  if (!category) {
    return CATEGORY_META.OTHER;
  }

  return CATEGORY_META[category] ?? CATEGORY_META.OTHER;
};

const MediaFallback: React.FC<{
  category?: RecommendationCategory | null;
  minHeightClassName?: string;
}> = ({ category, minHeightClassName = "min-h-[120px]" }) => {
  const categoryMeta = getCategoryMeta(category);
  const CategoryIcon = categoryMeta.icon;

  return (
    <div
      className={`w-full h-full ${minHeightClassName} bg-[#3E4F3C] border border-white/30 rounded-lg flex items-center justify-center`}
      aria-label="No media available"
    >
      <div className="w-14 h-14 rounded-full flex items-center justify-center bg-[#F9F6EC]/80 text-[#3E4F3C]">
        <CategoryIcon className="w-7 h-7" aria-hidden="true" />
      </div>
    </div>
  );
};

const ItemMedia: React.FC<{
  item: RecommendationItem;
  category?: RecommendationCategory | null;
  minHeightClassName?: string;
}> = ({ item, category, minHeightClassName = "min-h-[120px]" }) => {
  if (!item.mediaUrl) {
    return (
      <MediaFallback
        category={category}
        minHeightClassName={minHeightClassName}
      />
    );
  }

  if (item.mediaType === "VIDEO") {
    return (
      <div className="relative w-full">
        <video
          className={`w-full h-full ${minHeightClassName} object-cover rounded-lg border border-white/30`}
          controls
          preload="metadata"
          aria-label={`${item.name} video preview`}
        >
          <source src={item.mediaUrl} />
          Your browser does not support video preview.
        </video>
        <div className="absolute top-2 right-2 rounded-full bg-black/60 text-white p-1.5">
          <Video className="w-4 h-4" aria-hidden="true" />
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full">
      <img
        src={item.mediaUrl}
        alt={`${item.name} preview`}
        className={`w-full h-full ${minHeightClassName} object-cover rounded-lg border border-white/30`}
      />
      <div className="absolute top-2 right-2 rounded-full bg-black/55 text-white p-1.5">
        <ImageIcon className="w-4 h-4" aria-hidden="true" />
      </div>
    </div>
  );
};

const Recommendations: React.FC<Props> = ({ eventId, recommendationData }) => {
  const [currentListIndex, setCurrentListIndex] = React.useState(0);
  const [isItemModalOpen, setIsItemModalOpen] = React.useState(false);
  const [selectedItemData, setSelectedItemData] = React.useState<{
    item: RecommendationItem;
    category?: RecommendationCategory | null;
  } | null>(null);
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);

  const recommendationLists = React.useMemo(
    (): RecommendationList[] =>
      (recommendationData?.getEventRecommendationLists ?? [])
        .filter((list): list is RouteRecommendationList => Boolean(list))
        .map((list) => ({
          id: String(list.id),
          name: list.name,
          category: list.category,
          description: list.description,
          items: (list.items ?? [])
            .filter((item): item is RouteRecommendationItem => Boolean(item))
            .map((item) => ({
              id: String(item.id),
              name: item.name,
              description: item.description,
              contactInfo: item.contactInfo,
              link: item.link,
              mediaUrl: item.mediaUrl,
              mediaType: item.mediaType,
              createdAt:
                typeof item.createdAt === "string"
                  ? item.createdAt
                  : String(item.createdAt),
              updatedAt:
                typeof item.updatedAt === "string"
                  ? item.updatedAt
                  : String(item.updatedAt),
            })),
          createdAt:
            typeof list.createdAt === "string"
              ? list.createdAt
              : String(list.createdAt),
          updatedAt:
            typeof list.updatedAt === "string"
              ? list.updatedAt
              : String(list.updatedAt),
        })),
    [recommendationData]
  );

  React.useEffect(() => {
    if (currentListIndex > recommendationLists.length - 1) {
      setCurrentListIndex(0);
    }
  }, [currentListIndex, recommendationLists.length]);

  React.useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft = 0;
    }
  }, [currentListIndex]);

  const openItemModal = (
    item: RecommendationItem,
    category?: RecommendationCategory | null
  ) => {
    setSelectedItemData({ item, category });
    setIsItemModalOpen(true);
  };

  if (!eventId || recommendationLists.length === 0) {
    return null;
  }

  const activeList = recommendationLists[currentListIndex];
  const ActiveCategoryIcon = activeList
    ? getCategoryMeta(activeList.category).icon
    : null;

  const goToPreviousList = () => {
    if (!recommendationLists.length) return;
    setCurrentListIndex((prev) =>
      prev === 0 ? recommendationLists.length - 1 : prev - 1
    );
  };

  const goToNextList = () => {
    if (!recommendationLists.length) return;
    setCurrentListIndex((prev) =>
      prev === recommendationLists.length - 1 ? 0 : prev + 1
    );
  };

  return (
    <section className="w-full py-20 md:py-24 border-b border-white/30 bg-[#3E4F3C]">
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        <div className="mb-8 md:mb-10 text-center">
          <h2 className="text-white text-5xl lg:text-6xl font-normal leading-tight tracking-wide font-allura">
            Recommendations
          </h2>
          <p className="md:text-lg text-white mt-2 font-lato opacity-90">
            Helpful places and services curated by the couple.
          </p>
        </div>

        <div className="space-y-8">
          <article className="rounded-2xl border border-[#3E4F3C]/20 overflow-hidden shadow-lg">
            <div className="flex flex-col lg:flex-row">
              <div className="lg:w-[35%] p-6 md:p-8 border-b lg:border-b-0 lg:border-r border-[#3E4F3C]/20 flex flex-col justify-center bg-white/40">
                <div className="flex flex-row lg:items-center gap-4 lg:gap-3">
                  <div className="w-12 h-12 my-auto rounded-full flex items-center justify-center bg-[#3E4F3C] text-[#F9F6EC] flex-shrink-0">
                    {ActiveCategoryIcon && (
                      <ActiveCategoryIcon className="w-6 h-6" />
                    )}
                  </div>
                  <div className="flex-1 flex flex-col gap-2 md:gap-0 lg:gap-0">
                    <h3 className="text-2xl md:text-3xl text-[#F9F6EC] font-allura leading-tight">
                      {toTitleCase(activeList.name)}
                    </h3>
                    <p className="text-sm md:text-base text-[#F9F6EC] font-lato opacity-90 leading-relaxed">
                      {activeList.description?.trim() || (
                        <span
                          className="block min-h-[24px]"
                          aria-hidden="true"
                        />
                      )}
                    </p>
                  </div>
                </div>
              </div>

              <div
                className="lg:w-[65%] p-6 md:p-8 bg-white/40 overflow-x-auto scrollbar-hide"
                ref={scrollContainerRef}
              >
                <div className="flex gap-4 pb-4 min-w-min">
                  {(activeList.items ?? []).map((item) => (
                    <div
                      key={item.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => openItemModal(item, activeList.category)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          openItemModal(item, activeList.category);
                        }
                      }}
                      className="w-56 flex-shrink-0 flex flex-col rounded-xl border border-[#3E4F3C]/20 bg-[#F9F6EC]/70 p-4 cursor-pointer transition-colors hover:bg-[#eeece5] shadow-md"
                      aria-label={`Open ${item.name} details`}
                    >
                      <div className="mb-4">
                        <ItemMedia
                          item={item}
                          category={activeList.category}
                          minHeightClassName="min-h-[140px]"
                        />
                      </div>
                      <div className="flex-1 flex flex-col">
                        <h4 className="text-base font-semibold text-[#3E4F3C] font-lato leading-snug mb-2">
                          {item.name}
                        </h4>
                        {(item.link || item.contactInfo) && (
                          <div className="flex flex-col gap-1 text-xs text-[#3E4F3C] font-lato mb-2">
                            {item.link && (
                              <span className="inline-flex items-center gap-1.5 break-all">
                                <ExternalLink
                                  className="w-3 h-3 flex-shrink-0"
                                  aria-hidden="true"
                                />
                                <span className="truncate">{item.link}</span>
                              </span>
                            )}
                            {item.contactInfo && (
                              <span className="inline-flex items-center gap-1.5">
                                <Phone
                                  className="w-3 h-3 flex-shrink-0"
                                  aria-hidden="true"
                                />
                                <span className="truncate">
                                  {item.contactInfo}
                                </span>
                              </span>
                            )}
                          </div>
                        )}
                        {item.description?.trim() && (
                          <p className="text-xs text-[#3E4F3C] opacity-80 font-lato line-clamp-3">
                            {item.description}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </article>

          <div className="flex items-center justify-center gap-3 md:gap-4">
            <button
              type="button"
              onClick={goToPreviousList}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#F9F6EC]/40 text-[#F9F6EC] hover:bg-[#F9F6EC] hover:text-[#3E4F3C] transition-colors"
              aria-label="Previous recommendation list"
            >
              <ChevronLeft className="w-5 h-5" aria-hidden="true" />
            </button>

            <p className="text-sm text-[#F9F6EC] font-lato opacity-90">
              {currentListIndex + 1} / {recommendationLists.length}
            </p>

            <button
              type="button"
              onClick={goToNextList}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#F9F6EC]/40 text-[#F9F6EC] hover:bg-[#F9F6EC] hover:text-[#3E4F3C] transition-colors"
              aria-label="Next recommendation list"
            >
              <ChevronRight className="w-5 h-5" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>

      <Dialog
        open={isItemModalOpen}
        onOpenChange={setIsItemModalOpen}
        preventCloseAutoFocus
        trigger={<span className="hidden" aria-hidden="true" />}
      >
        {selectedItemData && (
          <div className="w-full max-w-[740px] lg:w-[40vw] lg:max-w-[40vw] mx-auto bg-[#FCF8EB] p-5 md:p-6 rounded-none md:rounded-2xl border-0 md:border md:border-[#EAE4CC]/40 shadow-none md:shadow-xl">
            <div className="flex flex-col gap-4 md:gap-5">
              <div className="w-full">
                <ItemMedia
                  item={selectedItemData.item}
                  category={selectedItemData.category}
                  minHeightClassName="min-h-[160px] lg:min-h-[260px]"
                />
              </div>

              <h3 className="text-xl md:text-2xl font-semibold text-[#3E4F3C] leading-snug font-dacing">
                {selectedItemData.item.name}
              </h3>

              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm md:text-base text-[#3E4F3C] font-serif">
                {selectedItemData.item.link ? (
                  <a
                    href={selectedItemData.item.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-[#3E4F3C] hover:underline break-all"
                  >
                    <ExternalLink
                      className="w-4 h-4 md:w-5 md:h-5 flex-shrink-0"
                      aria-hidden="true"
                    />
                    {selectedItemData.item.link}
                  </a>
                ) : selectedItemData.item.contactInfo ? (
                  <p className="inline-flex items-center gap-2">
                    <Phone
                      className="w-4 h-4 text-[#3E4F3C]"
                      aria-hidden="true"
                    />
                    {selectedItemData.item.contactInfo}
                  </p>
                ) : (
                  <div
                    className="min-h-[24px] min-w-[140px]"
                    aria-hidden="true"
                  />
                )}

                {selectedItemData.item.link &&
                  (selectedItemData.item.contactInfo ? (
                    <p className="inline-flex items-center gap-2">
                      <Phone
                        className="w-4 h-4 text-[#3E4F3C]"
                        aria-hidden="true"
                      />
                      {selectedItemData.item.contactInfo}
                    </p>
                  ) : (
                    <div
                      className="min-h-[24px] min-w-[140px]"
                      aria-hidden="true"
                    />
                  ))}
              </div>

              <p className="text-sm md:text-base text-[#3E4F3C] leading-relaxed font-serif opacity-90">
                {selectedItemData.item.description?.trim() || (
                  <span className="block min-h-[24px]" aria-hidden="true" />
                )}
              </p>
            </div>
          </div>
        )}
      </Dialog>
    </section>
  );
};

export default Recommendations;
